/* eslint-disable @typescript-eslint/no-explicit-any */
import { useConfirmModal } from '@/components/ConfirmModalProvider';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import GMap from '@/components/ui/GMap';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { ButtonLink } from '@/components/ui/Link';
import { ShForm } from '@/components/ui/rjsf/ShForm';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { auditLogByEntityQuery } from '@/features/Devices/graphql/query';
import {
  AuditLog,
  AuditLogBoolExp,
  AuditLogInsertInput,
  AuditLogOrderBy,
  Device,
  OrderBy,
  Site,
  SitePkColumnsInput,
  SiteSetInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import useDataTable from '@/hooks/useDataTable';
import {
  AuditLogAction,
  AuditLogEntityType,
  AuditLogEntry,
} from '@/models/audit-log.models';
import { JSONConfig } from '@/models/device.models';
import { FeatureName } from '@/models/role.models';
import { UserData } from '@/models/site.models';
import {
  createAuditLogInput,
  createAuditLogTitle,
} from '@/utils/audit-log.function';
import { getWebappBuildVersion } from '@/utils/meta.function';
import hasPermission from '@/utils/permission.function';
import { uploadFileResourcesInObject } from '@/utils/resource';
import { mergeVersionedSchemas } from '@/utils/rjsf';
import Form from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { isEmpty, isEqual } from 'lodash';
import {
  Copy,
  ListPlus,
  ListX,
  Loader2,
  NavigationIcon,
  Pencil,
  Trash,
  Trash2,
} from 'lucide-react';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { AssignDeviceToSiteDialog } from '../components/AssignDeviceToSiteDialog';
import deviceSiteColumns from '../components/DeviceSiteColumns';
import SiteConfigForm from '../components/SiteConfigForm';
import {
  deleteSiteMutation,
  unassignedDevicesFromSiteMutation,
  updateSiteMutation,
} from '../graphql/mutation';
import { siteByPkQuery } from '../graphql/query';
import * as latestSchema from './EditSiteData/FormSchemaV1';

type SiteFormData = {
  name: string;
};

type AuditLogFilterInput = {
  where: AuditLogBoolExp;
  orderBy: AuditLogOrderBy[];
};

const EditSitePage: React.FC = () => {
  const [assignDeviceDialogOpen, setAssignDeviceDialogOpen] =
    useState<boolean>();
  const { id } = useParams();
  const configFormRef = useRef<Form<any, RJSFSchema, any>>(null);
  const [editLocation, setEditLocation] = useState<boolean>();
  const [editingSite, setEditingSite] = useState<boolean>();
  const [config, setConfig] = useState<JSONConfig>();
  const [site, setSite] = useState<Site>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [viewing, setViewing] = useState<boolean>(true);
  const navigate = useNavigate();
  const { openModal } = useConfirmModal();

  const [auditLogFilters, _setAuditLogFilters] = useState<AuditLogFilterInput>({
    orderBy: [
      {
        createdAt: OrderBy.Desc,
      },
    ],
    where: {
      _and: [
        {
          entities: {
            _contains: [
              {
                id: id,
              },
            ],
          },
        },
      ],
    },
  });
  const [auditHistory, setAuditHistory] = useState<AuditLog[]>();
  const { register, reset, getValues } = useForm<SiteFormData>({});

  const { data: siteQuery, refetch } = useQuery({
    queryKey: ['Site', id],
    queryFn: () =>
      execute({
        query: siteByPkQuery,
        variables: {
          id: id as string,
        },
      }),
    select: (data) => data,
    staleTime: 5000,
  });

  const { data: auditLogData, refetch: refetchLog } = useQuery({
    queryKey: ['AuditLog', auditLogFilters],
    queryFn: () =>
      execute({
        query: auditLogByEntityQuery,
        variables: {
          where: auditLogFilters.where,
          orderBy: auditLogFilters.orderBy,
        },
      }),
    select: (data) => data,
    enabled: hasPermission(FeatureName.VIEW_AUDIT_HISTORY),
    staleTime: 3000,
  });

  useEffect(() => {
    if (auditLogData) {
      setAuditHistory(auditLogData.auditLog as AuditLog[]);
    }
  }, [auditLogData]);

  const { mutateAsync: updateSite } = useMutation({
    mutationKey: ['UpdateSite'],
    mutationFn: ({
      id,
      setInput,
      auditLogInput,
    }: {
      id: string;
      setInput: SiteSetInput;
      auditLogInput: AuditLogInsertInput;
    }) => {
      const pkColumnInput: SitePkColumnsInput = {
        id,
      };
      return execute({
        query: updateSiteMutation,
        variables: {
          pkColumnInput,
          setInput,
          auditLogInput,
        },
      });
    },
    onSuccess: async () => {
      await refetch();
      await refetchLog();
      toast.success('Site updated successfully!', {
        duration: 3000,
      });
      if (setEditLocation) {
        setEditLocation(false);
      }
      if (setEditingSite) {
        setEditingSite(false);
      }
    },
  });

  const { mutateAsync: unassignDevices } = useMutation({
    mutationKey: ['UnassignDevicesFromSite'],
    mutationFn: ({
      deviceIds,
      auditLogInput,
    }: {
      deviceIds: string[];
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: unassignedDevicesFromSiteMutation,
        variables: {
          deviceIds,
          auditLogInput,
        },
      });
    },
    onSuccess: async () => {
      await refetch();
      await refetchLog();
      toast.success('Devices removed successfully!', {
        duration: 3000,
      });
    },
  });

  const { mutateAsync: deleteSite } = useMutation({
    mutationKey: ['DeleteSite'],
    mutationFn: async ({
      id,
      auditLogInput,
    }: {
      id: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: deleteSiteMutation,
        variables: {
          id,
          auditLogInput,
        },
      });
    },
    onSuccess: () => {
      void navigate('/sites');
      toast.success('Site deleted successfully!', {
        duration: 3000,
      });
    },
  });

  const {
    mutateAsync: uploadSiteDataFiles,
    isPending: uploadSiteDataFilesIsPending,
  } = useMutation({
    mutationKey: ['UploadSiteDataFiles'],
    mutationFn: async (userData: UserData) => {
      await uploadFileResourcesInObject(
        userData.notes?.data,
        site?.customerId as string,
      );
      const pkColumnInput: SitePkColumnsInput = { id: site?.id as string };
      return execute({
        query: updateSiteMutation,
        variables: {
          pkColumnInput,
          setInput: { userData },
          auditLogInput: createAuditLogInput({
            action: AuditLogAction.UPDATE_SITE_USER_DATA,
            entities: [
              {
                entity: AuditLogEntityType.SITE,
                id: site?.id as string,
              },
            ],
          }),
        },
      });
    },
    onSuccess: async () => {
      await refetch();
      toast.success('Site data updated successfully!', {
        duration: 3000,
      });
    },
  });

  const {
    reset: locationFormReset,
    getValues: locationFormValues,
    control: locationFormControl,
    formState: { isValid: isLocationFormValid, isDirty: isLocationFormDirty },
  } = useForm<{ geometry: GeoJSON.Geometry }>({});

  useEffect(() => {
    if (siteQuery?.siteByPk) {
      setSite(siteQuery.siteByPk as Site);
    }
  }, [siteQuery]);

  useEffect(() => {
    if (site) {
      const formData: SiteFormData = {
        name: site.name,
      };
      setConfig(site.config ?? {});
      reset(formData);
    }
  }, [site, reset]);

  useEffect(() => {
    if (site) {
      locationFormReset({
        geometry: site.geometry,
      });
    }
  }, [site, locationFormReset]);

  const table = useDataTable<Device>({
    data: site?.devices ?? [],
    columns: deviceSiteColumns(),
    rowCount: site?.devicesAggregate?.aggregate?.count ?? 0,
    sorting,
    pagination,
    setSorting,
    setPagination,
  });

  const handleEditLocationSubmit = async (
    event?: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    event?.preventDefault();
    const auditLogInput = createAuditLogInput({
      action: AuditLogAction.UPDATE_SITE_LOCATION,
      entities: [
        {
          id: site?.id as string,
          entity: AuditLogEntityType.SITE,
        },
      ],
    });
    await updateSite({
      id: site?.id as string,
      auditLogInput,
      setInput: {
        rowVersion: uuid(),
        ...locationFormValues(),
      },
    });
  };

  const handleSiteFormSubmit = async () => {
    const config = configFormRef.current?.state.formData;
    setConfig(config);
    const auditLogInput = createAuditLogInput({
      action: AuditLogAction.UPDATE_SITE,
      entities: [
        {
          id: site?.id as string,
          entity: AuditLogEntityType.SITE,
        },
      ],
    });
    await updateSite({
      id: site?.id as string,
      auditLogInput,
      setInput: {
        rowVersion: uuid(),
        ...getValues(),
        config,
      },
    });
  };

  const handleToggleEditSite = () => {
    reset();
    setEditingSite(!editingSite);
  };

  const undoChanges = () => {
    reset();
    setEditingSite(!editingSite);
  };

  const handleToggleDialogOpen = async (shouldRefetch?: boolean) => {
    if (shouldRefetch) {
      await refetch();
      await refetchLog();
    }
    setAssignDeviceDialogOpen(!assignDeviceDialogOpen);
  };

  const confirmUnassignDevices = () => {
    openModal(
      {
        message:
          'Are you sure you want to remove the selected devices fom the site?',
      },
      async () => {
        const deviceIds = table
          .getSelectedRowModel()
          .rows.map((row) => row.original.id);
        const log: AuditLogEntry = {
          action: AuditLogAction.UNASSIGN_DEVICE_FROM_SITE,
          entities: [
            {
              id: site?.id as string,
              entity: AuditLogEntityType.SITE,
            },
            ...deviceIds.map((deviceId) => ({
              id: deviceId,
              entity: AuditLogEntityType.DEVICE,
            })),
          ],
        };
        const auditLogInput = createAuditLogInput(log);
        await unassignDevices({ deviceIds, auditLogInput });
        table.resetRowSelection();
      },
    );
  };

  const handleEditLocation = () => {
    setEditLocation(!editLocation);
    if (editLocation) {
      locationFormReset();
    }
  };

  const handleDeleteSiteClick = () => {
    const canDelete = !site?.devicesAggregate?.aggregate?.count;
    const message = canDelete ? (
      <span>
        Are you sure you want to delete site <strong>{site?.name}</strong>?
      </span>
    ) : (
      <span>
        Site <strong>{site?.name}</strong> has associated devices. Please remove
        all devices before deleting the site.
      </span>
    );
    if (canDelete) {
      openModal(
        {
          message: message,
        },
        async () => {
          const auditLogInput = createAuditLogInput({
            action: AuditLogAction.DELETE_SITE,
            entities: [
              {
                id: site?.id as string,
                entity: AuditLogEntityType.SITE,
              },
            ],
          });
          await deleteSite({ id: site?.id as string, auditLogInput });
        },
      );
    } else {
      openModal({
        message: message,
        showConfirm: false,
        title: 'Delete Site',
        cancelText: 'Close',
      });
    }
  };

  const onSaveMeta = async (formData: any) => {
    const userData: UserData = {
      ...site?.userData,
      notes: isEmpty(formData)
        ? null
        : {
            ...currentSchema,
            data: formData,
            buildVersion: getWebappBuildVersion(),
          },
    };

    if (!isEqual(site?.userData, userData)) {
      await uploadSiteDataFiles(userData);
    }

    setViewing(true);
  };

  const onCancelMeta = () => {
    setViewing(true);
  };

  const onDeleteMeta = () => {
    openModal(
      {
        message: 'Are you sure you want to delete all metadata?',
      },
      async () => {
        await onSaveMeta(null);
        setViewing(true);
      },
      () => {},
    );
  };

  const existingNotes = (site?.userData as UserData)?.notes;
  const metaFormData = isEmpty(existingNotes?.data)
    ? null
    : existingNotes?.data;
  const currentSchema = mergeVersionedSchemas({
    existing: existingNotes,
    latest: latestSchema,
  });

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      {site ? (
        <>
          <div className="xs:flex-row xs:justify-between flex flex-col align-middle">
            <div>
              <div className="space-x-2">
                <span className="text-muted-foreground text-md font-normal tracking-tight">
                  Editing
                </span>
                <span className="text-xl font-semibold tracking-tight">
                  {site.name}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              {hasPermission(FeatureName.DELETE_SITE) && (
                <Button variant={'destructive'} onClick={handleDeleteSiteClick}>
                  <Trash />
                  Delete
                </Button>
              )}
            </div>
          </div>
          <Tabs defaultValue={'general'}>
            <TabsList className="flex justify-between gap-2">
              <TabsTrigger value="general" className="w-30">
                General
              </TabsTrigger>
              <TabsTrigger value="assignedDevices" className="w-30">
                Assigned Devices
              </TabsTrigger>
              {hasPermission(FeatureName.VIEW_EDIT_SITE_METADATA) && (
                <TabsTrigger value="metadata" className="w-30">
                  Metadata
                </TabsTrigger>
              )}
              {hasPermission(FeatureName.VIEW_AUDIT_HISTORY) && (
                <TabsTrigger value="auditHistory" className="w-30">
                  Audit History
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="general">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 flex flex-col gap-4 lg:col-span-1">
                  <Card className="relative rounded-md">
                    <CardHeader>
                      <CardTitle>Configuration</CardTitle>
                      {hasPermission(FeatureName.UPDATE_SITE) && (
                        <Button
                          className="absolute top-4 right-4"
                          variant={'outline'}
                          onClick={handleToggleEditSite}
                        >
                          {editingSite ? (
                            'Cancel Editing '
                          ) : (
                            <>
                              <Pencil />
                              Edit
                            </>
                          )}
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent className="grid items-start gap-6">
                      <div className="grid gap-2">
                        <Label htmlFor="customer">Customer</Label>
                        <div className="flex space-x-2">
                          <Input
                            type="text"
                            id="customer"
                            readOnly
                            placeholder="Customer name"
                            defaultValue={site.customer.name}
                          />
                          {hasPermission(
                            FeatureName.VIEW_EDIT_CUSTOMER_PAGE,
                          ) && (
                            <ButtonLink
                              variant={'outline'}
                              to={`/customers/${site.customer.id}`}
                            >
                              <NavigationIcon />
                            </ButtonLink>
                          )}
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="site-id">Site Id</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            id="site-id"
                            readOnly
                            placeholder="Site id"
                            defaultValue={site.id}
                            className="truncate pr-8"
                          />
                          <Button
                            type="button"
                            className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
                            size={'icon'}
                            variant={'link'}
                            onClick={() => {
                              toast.message('Copied!', { duration: 1000 });
                              void navigator.clipboard.writeText(site.id);
                            }}
                          >
                            <Copy className="size-5" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                          type="text"
                          id="name"
                          readOnly={
                            !editingSite ||
                            !hasPermission(FeatureName.UPDATE_SITE)
                          }
                          placeholder="Site name"
                          {...register('name', {
                            required: 'Name is required',
                          })}
                        />
                      </div>
                      <SiteConfigForm
                        ref={configFormRef}
                        formData={config}
                        readonly={
                          !editingSite ||
                          !hasPermission(FeatureName.UPDATE_SITE)
                        }
                      />
                    </CardContent>
                    {editingSite && (
                      <CardFooter>
                        <div className="mt-4 flex w-full justify-end gap-4">
                          <Button
                            variant={'default'}
                            className="w-30"
                            size={'sm'}
                            onClick={() => void handleSiteFormSubmit()}
                          >
                            Save
                          </Button>
                          <Button
                            variant={'outline'}
                            className="w-30"
                            size={'sm'}
                            onClick={undoChanges}
                          >
                            Cancel
                          </Button>
                        </div>
                      </CardFooter>
                    )}
                  </Card>
                </div>

                <div className="col-span-2 lg:col-span-1">
                  <Card className="relative h-full">
                    <CardHeader>
                      <CardTitle>Location</CardTitle>
                      {hasPermission(
                        FeatureName.CREATE_UPDATE_SITE_LOCATION,
                      ) && (
                        <Button
                          className="absolute top-4 right-4"
                          variant={'outline'}
                          size={'sm'}
                          onClick={handleEditLocation}
                        >
                          {editLocation ? (
                            'Cancel Editing '
                          ) : (
                            <>
                              <Pencil />
                              Edit
                            </>
                          )}
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                        }}
                      >
                        <div className="flex justify-center">
                          <Controller
                            name="geometry"
                            control={locationFormControl}
                            render={({ field }) => (
                              <GMap
                                draw={editLocation}
                                editingGeometry={field.value}
                                onEditGeometryChange={field.onChange}
                              />
                            )}
                          />
                        </div>
                      </form>

                      {isLocationFormDirty && (
                        <CardFooter>
                          <div className="mt-4 flex w-full justify-end gap-4">
                            <Button
                              variant={'default'}
                              disabled={!isLocationFormValid}
                              className="w-30"
                              type="button"
                              size={'sm'}
                              onClick={(e) => void handleEditLocationSubmit(e)}
                            >
                              Save
                            </Button>
                            <Button
                              variant={'outline'}
                              className="w-30"
                              type="button"
                              size={'sm'}
                              disabled={!isLocationFormDirty}
                              onClick={() => {
                                locationFormReset();
                                setEditLocation(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </CardFooter>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="assignedDevices">
              <Card className="relative">
                <CardHeader>
                  <CardTitle>Assigned Devices</CardTitle>
                  <CardDescription>
                    Devices assigned to the site
                  </CardDescription>
                  {hasPermission(FeatureName.ASSIGN_DEVICES_TO_SITE) && (
                    <div className="absolute top-4 right-4 flex gap-2">
                      <Button
                        variant={'outline'}
                        size={'sm'}
                        onClick={() => setAssignDeviceDialogOpen(true)}
                      >
                        <ListPlus />
                        Assign Device
                      </Button>
                      <Button
                        variant={'outline'}
                        size={'sm'}
                        onClick={confirmUnassignDevices}
                        disabled={table.getSelectedRowModel().rows.length === 0}
                      >
                        <ListX />
                        Unassign Devices
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <DataTable table={table} selectRowEnabled={true} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="metadata">
              <Card className="h-full rounded-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Metadata</CardTitle>
                    {hasPermission(FeatureName.UPDATE_SITE) && (
                      <>
                        {viewing ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setViewing(false)}
                          >
                            Edit
                          </Button>
                        ) : (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={onDeleteMeta}
                            className="ml-1"
                          >
                            <Trash2 className="ml-2 h-4 w-4" />
                            Delete
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="grid items-start gap-6">
                  {viewing && !metaFormData ? (
                    <div className="text-muted-foreground">Empty</div>
                  ) : (
                    <ShForm
                      schema={currentSchema.schema}
                      uiSchema={{
                        ...currentSchema.uiSchema,
                        'ui:submitButtonOptions': { norender: true },
                      }}
                      validator={validator}
                      formData={metaFormData}
                      onSubmit={(data) => void onSaveMeta(data.formData)}
                      readonly={viewing}
                      startIndentLevel={2}
                    >
                      {!viewing && (
                        <div className="flex justify-end gap-2">
                          <Button
                            type="submit"
                            variant="default"
                            disabled={uploadSiteDataFilesIsPending}
                          >
                            {uploadSiteDataFilesIsPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              'Submit'
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={onCancelMeta}
                            disabled={uploadSiteDataFilesIsPending}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </ShForm>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {hasPermission(FeatureName.VIEW_AUDIT_HISTORY) && (
              <TabsContent value="auditHistory">
                <Card className="relative">
                  <CardHeader>
                    <CardTitle>Audit History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-2">
                      {auditHistory?.map((audit) => {
                        return (
                          <div key={audit.id}>{createAuditLogTitle(audit)}</div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>

          {assignDeviceDialogOpen && (
            <AssignDeviceToSiteDialog
              site={site}
              onToggleOpen={(refetch) => void handleToggleDialogOpen(refetch)}
              open={assignDeviceDialogOpen}
            />
          )}
        </>
      ) : (
        <></>
      )}
    </ErrorBoundary>
  );
};

export default EditSitePage;
